import React, { useState, useEffect } from 'react';
import { Execution } from '../types/executions';
import { executionService } from '../services/executionService';
import ExecutionJobsList from './ExecutionJobsList';

interface JobsStats {
  total: number;
  completed: number;
}

const ExecutionManager: React.FC = () => {
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedExecutions, setExpandedExecutions] = useState<Set<string>>(new Set());
  const [jobsStats, setJobsStats] = useState<Record<string, JobsStats>>({});
  const [openActionMenu, setOpenActionMenu] = useState<string | null>(null);
  const [refreshingIds, setRefreshingIds] = useState<Set<string>>(new Set());
  const [jobsRefreshTrigger, setJobsRefreshTrigger] = useState<Record<string, number>>({});

  useEffect(() => {
    loadData();
  }, []);


  const loadJobsStats = async (executionId: string) => {
    try {
      const res = await fetch(`/api/jobs?executionId=${executionId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('id_token')}` }
      });
      if (res.ok) {
        const jobs = await res.json();
        const completed = jobs.filter((job: any) => job.status === 'completed').length;
        setJobsStats(prev => ({
          ...prev,
          [executionId]: { total: jobs.length, completed }
        }));
      }
    } catch (error) {
      console.error('Failed to load jobs stats:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const executionsData = await executionService.getAll();
      setExecutions(executionsData);
      // Load jobs stats for each execution
      executionsData.forEach(exec => {
        loadJobsStats(exec.id);
      });
    } catch (err) {
      setError('Failed to load data');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };


  const refreshExecution = async (executionId: string) => {
    setRefreshingIds(prev => new Set(prev).add(executionId));
    try {
      const updatedExecution = await executionService.getById(executionId);
      setExecutions(prev =>
        prev.map(exec => (exec.id === executionId ? updatedExecution : exec))
      );
      await loadJobsStats(executionId);
      setJobsRefreshTrigger(prev => ({
        ...prev,
        [executionId]: (prev[executionId] || 0) + 1
      }));
    } catch (err) {
      console.error('Error refreshing execution:', err);
    } finally {
      setRefreshingIds(prev => {
        const next = new Set(prev);
        next.delete(executionId);
        return next;
      });
    }
  };

  const cancelExecution = async (executionId: string) => {
    try {
      const updatedExecution = await executionService.update(executionId, 'cancel');
      setExecutions(prev =>
        prev.map(exec =>
          exec.id === executionId ? updatedExecution : exec
        )
      );
    } catch (err) {
      console.error('Error cancelling execution:', err);
      setError('Failed to cancel execution');
    }
  };


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openActionMenu && !(event.target as Element).closest('.execution-actions-menu-wrapper')) {
        setOpenActionMenu(null);
    }
    };

    if (openActionMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openActionMenu]);

  const toggleExpand = (executionId: string) => {
    setExpandedExecutions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(executionId)) {
        newSet.delete(executionId);
      } else {
        newSet.add(executionId);
      }
      return newSet;
    });
  };

  const toggleActionMenu = (executionId: string) => {
    setOpenActionMenu(prev => prev === executionId ? null : executionId);
  };

  const handleCancelFromMenu = async (executionId: string) => {
    await cancelExecution(executionId);
    setOpenActionMenu(null);
  };


  const getStatusColor = (status: Execution['status'] | string) => {
    switch (status.toLowerCase()) {
      case 'pending': return '#f59e0b';
      case 'running': return '#3b82f6';
      case 'completed': return '#10b981';
      case 'failed':
      case 'error': return '#ef4444';
      case 'cancelled': return '#64748b';
      default: return '#64748b';
    }
  };

  const formatPipelineType = (type: string) => {
    return type.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatDate = (date: Date | string) => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${day}.${month}.${year}, ${hours}:${minutes}:${seconds}`;
  };

  const formatDateOnly = (date: Date | string) => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const canCancel = (execution: Execution) => {
    return ['pending', 'running'].includes(execution.status);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <span>Loading executions...</span>
      </div>
    );
  }
  if (error) return <div className="error-alert">{error}</div>;

  return (
    <div className="execution-manager">
      {executions.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">⚡</div>
          <div className="empty-state-title">No executions found</div>
          <div className="empty-state-text">Executions are created when you run a pipeline. Go to the Pipelines page to create and run your first execution.</div>
        </div>
      ) : (
        <div className="execution-list">
          {executions.map(execution => (
            <div key={execution.id} className="execution-item">
              <div className="execution-main-content">
              <div className="execution-info">
                <div className="execution-header-info">
                    <div className="execution-title-section">
                      <h4 className="execution-title">
                        <span className="execution-type">{formatPipelineType(execution.pipelineType)}</span>
                        {execution.pipelineName}
                      </h4>
                    </div>
                    <div className="execution-header-actions">
                      <span className="execution-status-badge" style={{ backgroundColor: getStatusColor(execution.status) }}>
                    {execution.status.toUpperCase()}
                  </span>
                      {canCancel(execution) && (
                        <div className="execution-actions-menu-wrapper">
                          <button
                            onClick={() => toggleActionMenu(execution.id)}
                            className="execution-actions-menu-button"
                            title="Actions"
                          >
                            ⋯
                          </button>
                          {openActionMenu === execution.id && (
                            <div className="execution-actions-dropdown">
                              <button
                                className="execution-actions-dropdown-item"
                                onClick={() => handleCancelFromMenu(execution.id)}
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                </div>
                <div className="execution-details">
                    {execution.parameters?.startDate && execution.parameters?.endDate && (
                      <p className="execution-date-range">
                        <strong>Date Range:</strong> {formatDateOnly(execution.parameters.startDate)} - {formatDateOnly(execution.parameters.endDate)}
                      </p>
                    )}
                    <div className="execution-timestamps">
                  <p><strong>Created:</strong> {formatDate(execution.createdAt)}</p>
                  {execution.startedAt && (
                    <p><strong>Started:</strong> {formatDate(execution.startedAt)}</p>
                  )}
                  {execution.completedAt && (
                    <p><strong>Completed:</strong> {formatDate(execution.completedAt)}</p>
                  )}
                      {execution.scheduledAt && (
                        <p><strong>Scheduled:</strong> {formatDate(execution.scheduledAt)}</p>
                      )}
                    </div>
                    <div className="execution-other-info">
                      <p><strong>Triggered by:</strong> {execution.triggeredBy}</p>
                  {execution.error && (
                    <p className="execution-error">
                      <strong>Error:</strong> {typeof execution.error === 'object' ? (execution.error as any).errorMessage || JSON.stringify(execution.error) : execution.error}
                    </p>
                  )}
                  {execution.progress !== undefined && execution.status === 'running' && (
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${execution.progress}%` }}
                      ></div>
                      <span className="progress-text">{execution.progress}%</span>
                    </div>
                  )}
                </div>
              </div>
                </div>
                <div className="execution-bottom-actions">
                  <button
                    onClick={() => refreshExecution(execution.id)}
                    className="execution-refresh-button"
                    title="Refresh"
                    disabled={refreshingIds.has(execution.id)}
                  >
                    <span className={refreshingIds.has(execution.id) ? 'spin' : ''}>⟳</span>
                    {' '}Refresh
                  </button>
                  {jobsStats[execution.id] && jobsStats[execution.id].total > 0 && (
                <button
                  onClick={() => toggleExpand(execution.id)}
                      className="execution-jobs-toggle-button"
                >
                  {expandedExecutions.has(execution.id) ? 'Hide Jobs' : 'View Jobs'}
                      <span className="execution-jobs-stats-inline">
                        {' '}({jobsStats[execution.id].completed}/{jobsStats[execution.id].total})
                      </span>
                </button>
                  )}
                </div>
              </div>
              {expandedExecutions.has(execution.id) && (
                <div className="execution-jobs-container">
                <ExecutionJobsList
                  executionId={execution.id}
                  refreshTrigger={jobsRefreshTrigger[execution.id]}
                />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


export default ExecutionManager;
